import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6jnocfgl4s4nd8dm26g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column */}
          <div className="text-white">
            <h2 className="text-5xl font-bold mb-6">
              Ready to Schedule Service?
            </h2>
            <p className="text-xl text-gray-300 mb-12">
              Our team is standing by to help with all your HVAC needs
            </p>

            {/* Phone */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-2xl text-white"></i>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Call Us Now</p>
                  <a 
                    href="tel:9412233052" 
                    className="text-4xl font-bold text-orange-500 hover:text-orange-400 transition-colors cursor-pointer"
                  >
                    (941) 223-3052
                  </a>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-fill text-2xl text-white"></i>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Email Us</p>
                  <a 
                    href="mailto:scdoolittle5@comcast.net" 
                    className="text-xl text-white hover:text-orange-500 transition-colors cursor-pointer"
                  >
                    scdoolittle5@comcast.net
                  </a>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-fill text-2xl text-white"></i>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Business Hours</p>
                  <p className="text-xl text-white">Mon-Fri: 7AM - 7PM</p>
                  <p className="text-xl text-white">Sat: 8AM - 5PM</p>
                  <p className="text-lg text-orange-500 font-semibold mt-1">Emergency Service Available</p>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="flex gap-4 mt-12">
              <a 
                href="#" 
                className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-orange-500 hover:border-orange-500 transition-all cursor-pointer"
              >
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a 
                href="#" 
                className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-orange-500 hover:border-orange-500 transition-all cursor-pointer"
              >
                <i className="ri-instagram-fill text-xl"></i>
              </a>
              <a 
                href="#" 
                className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-orange-500 hover:border-orange-500 transition-all cursor-pointer"
              >
                <i className="ri-google-fill text-xl"></i>
              </a>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-white rounded-3xl p-10 shadow-2xl">
            <form id="service-request-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-base"
                  placeholder="John Smith"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-base"
                  placeholder="(941) 555-5555"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-base"
                  placeholder="john@example.com"
                />
              </div>

              {/* Service Needed */}
              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                  Service Needed *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors text-base cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="AC Repair">AC Repair</option>
                  <option value="AC Installation">AC Installation & Replacement</option>
                  <option value="HVAC Maintenance">HVAC Maintenance</option>
                  <option value="Duct Cleaning">Duct Cleaning</option>
                  <option value="Dryer Vent Cleaning">Dryer Vent Cleaning</option>
                  <option value="Mini Split Installation">Mini Split Installation</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none transition-colors resize-none text-base"
                  placeholder="Tell us about your HVAC needs..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-orange-500 text-white text-lg font-bold rounded-lg hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    REQUEST SERVICE
                    <i className="ri-arrow-right-line text-xl"></i>
                  </>
                )}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 text-center">
                  <i className="ri-check-line text-2xl text-green-500 mb-2"></i>
                  <p className="text-green-700 font-semibold">
                    Thank you! We'll respond within 1 hour during business hours.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 text-center">
                  <i className="ri-error-warning-line text-2xl text-red-500 mb-2"></i>
                  <p className="text-red-700 font-semibold">
                    Something went wrong. Please call us at (941) 223-3052.
                  </p>
                </div>
              )}

              <p className="text-sm text-gray-500 text-center">
                We'll respond within 1 hour during business hours
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}